import React, { useState, useEffect } from 'react'
import { 
  Save,
  RotateCcw,
  Plus,
  Trash2,
  Receipt,
  Search,
  Upload,
  FileText,
  Download,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import expenseClaimService from '../../services/expenseClaimService'
import employeeMasterService from '../../services/employeeMasterService'
import expenseCategoryMasterService from '../../services/expenseCategoryMasterService'
import paymentMethodMasterService from '../../services/paymentMethodMasterService'
import ProjectSelectionModal from '../../components/ProjectSelectionModal/ProjectSelectionModal'
import api from '../../services/api'
import '../../styles/expenseClaimForm.css'
import '../Accounts/PaymentForm.css'
import { formatCurrency } from '../../utils/currencyFormat'

const ExpenseClaimForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = location.pathname.includes('/edit')
  const isView = location.pathname.includes('/view')
  const claimData = location.state?.claimData

  const [formData, setFormData] = useState({
    claimNumber: '',
    employee: '',
    employeeName: '',
    employeeCode: '',
    department: '',
    claimDate: new Date().toISOString().split('T')[0],
    expenseDate: new Date().toISOString().split('T')[0],
    description: '',
    expenseItems: [],
    totalAmount: 0,
    totalTaxAmount: 0,
    grandTotal: 0,
    currency: 'INR',
    status: 'Draft',
    projectCode: '',
    projectName: '',
    clientCode: '',
    clientName: '',
    isBillable: false,
    notes: '',
    attachments: []
  })

  const [isLoadingClaimNumber, setIsLoadingClaimNumber] = useState(true)
  const [employees, setEmployees] = useState([])
  const [categories, setCategories] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [loading, setLoading] = useState(false)
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)

  // Load initial data
  useEffect(() => {
    if (!isEdit && !isView) {
      fetchClaimNumber()
    } else if (claimData) {
      // Populate form with existing data
      setFormData({
        claimNumber: claimData.claimNumber || '',
        employee: claimData.employee?._id || claimData.employee || '',
        employeeName: claimData.employeeName || '',
        employeeCode: claimData.employeeCode || '',
        department: claimData.department || '',
        claimDate: claimData.claimDate ? new Date(claimData.claimDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        expenseDate: claimData.expenseDate ? new Date(claimData.expenseDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        description: claimData.description || '',
        expenseItems: claimData.expenseItems || [],
        totalAmount: claimData.totalAmount || 0,
        totalTaxAmount: claimData.totalTaxAmount || 0,
        grandTotal: claimData.grandTotal || 0,
        currency: claimData.currency || 'INR',
        status: claimData.status || 'Draft',
        projectCode: claimData.projectCode || '',
        projectName: claimData.projectName || '',
        clientCode: claimData.clientCode || '',
        clientName: claimData.clientName || '',
        isBillable: claimData.isBillable || false,
        notes: claimData.notes || '',
        attachments: claimData.attachments || []
      })
      setIsLoadingClaimNumber(false)
    }

    // Load dropdown data
    loadDropdownData()
  }, [])

  const fetchClaimNumber = async () => {
    try {
      setIsLoadingClaimNumber(true)
      const response = await expenseClaimService.getNextClaimNumber()
      if (response.success && response.data) {
        setFormData(prev => ({
          ...prev,
          claimNumber: response.data.claimNumber || response.data.formattedValue || 'ECLM0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching claim number:', error)
      setFormData(prev => ({ ...prev, claimNumber: 'ECLM0001' }))
    } finally {
      setIsLoadingClaimNumber(false)
    }
  }

  const loadDropdownData = async () => {
    try {
      const [employeesRes, categoriesRes, paymentMethodsRes] = await Promise.all([
        employeeMasterService.getAllEmployees(),
        expenseCategoryMasterService.getActiveCategories(),
        paymentMethodMasterService.getActivePaymentMethods()
      ])

      if (employeesRes.success) {
        setEmployees(employeesRes.data || [])
      }
      if (categoriesRes.success) {
        setCategories(categoriesRes.data || [])
      }
      if (paymentMethodsRes.success) {
        setPaymentMethods(paymentMethodsRes.data || [])
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Update employee details when employee is selected
    if (name === 'employee') {
      const selectedEmployee = employees.find(emp => emp._id === value)
      if (selectedEmployee) {
        setFormData(prev => ({
          ...prev,
          employeeName: selectedEmployee.employeeName || '',
          employeeCode: selectedEmployee.employeeCode || '',
          department: selectedEmployee.department || ''
        }))
      }
    }
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.expenseItems]
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === 'amount' || field === 'taxAmount' ? parseFloat(value) || 0 : value
    }

    // Calculate total amount for this item
    const amount = updatedItems[index].amount || 0
    const taxAmount = updatedItems[index].taxAmount || 0
    updatedItems[index].totalAmount = amount + taxAmount

    // Update category name when category is selected
    if (field === 'category') {
      const selectedCategory = categories.find(cat => cat._id === value)
      if (selectedCategory) {
        updatedItems[index].categoryName = selectedCategory.categoryName || ''
      }
    }

    // Update payment method name when payment method is selected
    if (field === 'paymentMethod') {
      const selectedMethod = paymentMethods.find(method => method._id === value)
      if (selectedMethod) {
        updatedItems[index].paymentMethodName = selectedMethod.paymentMethodName || ''
      }
    }

    setFormData(prev => {
      const newItems = updatedItems
      const totalAmount = newItems.reduce((sum, item) => sum + (item.amount || 0), 0)
      const totalTaxAmount = newItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0)
      const grandTotal = totalAmount + totalTaxAmount

      return {
        ...prev,
        expenseItems: newItems,
        totalAmount,
        totalTaxAmount,
        grandTotal
      }
    })
  }

  const addExpenseItem = () => {
    setFormData(prev => ({
      ...prev,
      expenseItems: [
        ...prev.expenseItems,
        {
          itemNumber: prev.expenseItems.length + 1,
          category: '',
          categoryName: '',
          description: '',
          amount: 0,
          currency: prev.currency,
          taxAmount: 0,
          totalAmount: 0,
          paymentMethod: '',
          paymentMethodName: '',
          receiptNumber: '',
          receiptDate: '',
          receiptAttached: false,
          notes: ''
        }
      ]
    }))
  }

  const removeExpenseItem = (index) => {
    const updatedItems = formData.expenseItems.filter((_, i) => i !== index)
    // Renumber items
    updatedItems.forEach((item, i) => {
      item.itemNumber = i + 1
    })

    const totalAmount = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0)
    const totalTaxAmount = updatedItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0)
    const grandTotal = totalAmount + totalTaxAmount

    setFormData(prev => ({
      ...prev,
      expenseItems: updatedItems,
      totalAmount,
      totalTaxAmount,
      grandTotal
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.employee) {
        toast.error('Please select an employee')
        return
      }
      if (formData.expenseItems.length === 0) {
        toast.error('Please add at least one expense item')
        return
      }

      setLoading(true)
      let response
      if (isEdit) {
        response = await expenseClaimService.updateClaim(claimData._id, formData)
      } else {
        response = await expenseClaimService.createClaim(formData)
      }

      if (response.success) {
        toast.success(`Expense claim ${isEdit ? 'updated' : 'created'} successfully!`)
        navigate('/expenses/expense-claim')
      } else {
        toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} expense claim`)
      }
    } catch (error) {
      console.error('Error saving expense claim:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        toast.error(`Failed to ${isEdit ? 'update' : 'create'} expense claim`)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleTestData = () => {
    const today = new Date()
    const claimDate = today.toISOString().split('T')[0]
    const expenseDate = new Date(today)
    expenseDate.setDate(today.getDate() - 5) // Expense date 5 days ago
    
    const testEmployee = employees.length > 0 ? employees[0] : null
    const testCategory1 = categories.length > 0 ? categories[0] : null
    const testCategory2 = categories.length > 1 ? categories[1] : null
    const testCategory3 = categories.length > 2 ? categories[2] : null
    const testPaymentMethod = paymentMethods.length > 0 ? paymentMethods[0] : null

    const testExpenseItems = []
    
    // Add first expense item
    if (testCategory1) {
      const amount1 = 5000
      const taxAmount1 = 900 // 18% GST
      testExpenseItems.push({
        itemNumber: 1,
        category: testCategory1._id || testCategory1.id,
        categoryName: testCategory1.categoryName || 'Travel',
        description: 'Flight tickets - Mumbai to Delhi',
        amount: amount1,
        currency: 'INR',
        taxAmount: taxAmount1,
        totalAmount: amount1 + taxAmount1,
        paymentMethod: testPaymentMethod ? (testPaymentMethod._id || testPaymentMethod.id) : '',
        paymentMethodName: testPaymentMethod?.paymentMethodName || 'Credit Card',
        receiptNumber: 'RCP-001234',
        receiptDate: expenseDate.toISOString().split('T')[0],
        receiptAttached: true,
        notes: 'Business travel expense'
      })
    }

    // Add second expense item
    if (testCategory2) {
      const amount2 = 3000
      const taxAmount2 = 540 // 18% GST
      testExpenseItems.push({
        itemNumber: 2,
        category: testCategory2._id || testCategory2.id,
        categoryName: testCategory2.categoryName || 'Accommodation',
        description: 'Hotel accommodation - 2 nights',
        amount: amount2,
        currency: 'INR',
        taxAmount: taxAmount2,
        totalAmount: amount2 + taxAmount2,
        paymentMethod: testPaymentMethod ? (testPaymentMethod._id || testPaymentMethod.id) : '',
        paymentMethodName: testPaymentMethod?.paymentMethodName || 'Credit Card',
        receiptNumber: 'RCP-001235',
        receiptDate: expenseDate.toISOString().split('T')[0],
        receiptAttached: true,
        notes: 'Hotel stay during business trip'
      })
    }

    // Add third expense item
    if (testCategory3) {
      const amount3 = 2000
      const taxAmount3 = 360 // 18% GST
      testExpenseItems.push({
        itemNumber: 3,
        category: testCategory3._id || testCategory3.id,
        categoryName: testCategory3.categoryName || 'Meals',
        description: 'Business meals and client entertainment',
        amount: amount3,
        currency: 'INR',
        taxAmount: taxAmount3,
        totalAmount: amount3 + taxAmount3,
        paymentMethod: testPaymentMethod ? (testPaymentMethod._id || testPaymentMethod.id) : '',
        paymentMethodName: testPaymentMethod?.paymentMethodName || 'Credit Card',
        receiptNumber: 'RCP-001236',
        receiptDate: expenseDate.toISOString().split('T')[0],
        receiptAttached: true,
        notes: 'Client meeting expenses'
      })
    }

    // Calculate totals
    const totalAmount = testExpenseItems.reduce((sum, item) => sum + (item.amount || 0), 0)
    const totalTaxAmount = testExpenseItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0)
    const grandTotal = totalAmount + totalTaxAmount

    setFormData(prev => ({
      ...prev,
      employee: testEmployee ? (testEmployee._id || testEmployee.id) : '',
      employeeName: testEmployee?.employeeName || testEmployee?.firstName + ' ' + testEmployee?.lastName || 'Test Employee',
      employeeCode: testEmployee?.employeeCode || testEmployee?.code || '',
      department: testEmployee?.department || 'IT',
      claimDate: claimDate,
      expenseDate: expenseDate.toISOString().split('T')[0],
      description: 'Business travel expenses for client meeting in Delhi. Includes flight tickets, hotel accommodation, and meals.',
      expenseItems: testExpenseItems,
      totalAmount: totalAmount,
      totalTaxAmount: totalTaxAmount,
      grandTotal: grandTotal,
      currency: 'INR',
      status: 'Draft',
      projectCode: '', // Leave empty - should be selected via modal
      projectName: '', // Leave empty - should be selected via modal
      clientCode: '', // Leave empty - should be selected via modal
      clientName: '', // Leave empty - should be selected via modal
      isBillable: false,
      notes: 'Test expense claim data for demonstration purposes. All receipts are attached.'
    }))
    
    toast.success('Test data loaded successfully!')
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEdit || isView) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEdit, isView, employees, categories, paymentMethods]) // Include dependencies used by handleTestData

  const handleProjectSearch = () => {
    setShowProjectModal(true)
  }

  const handleProjectSelect = (selectedProject) => {
    if (!selectedProject) {
      return
    }

    setFormData(prev => ({
      ...prev,
      projectCode: selectedProject.projectNumber || '',
      projectName: selectedProject.projectName || '',
      clientCode: selectedProject.customerCode || '',
      clientName: selectedProject.customerName || ''
    }))
    setShowProjectModal(false)
    toast.success('Project selected successfully!')
  }

  const handleFileUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB')
      return
    }

    // Validate file type
    const allowedTypes = [
      'image/svg+xml',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only SVG, PNG, JPG, PDF, DOC, DOCX files are allowed.')
      return
    }

    try {
      setUploadingFile(true)
      const uploadFormData = new FormData()
      uploadFormData.append('file', file)
      uploadFormData.append('documentType', 'expense-claim-attachment')

      const response = await api.post('/upload/document', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      const result = response.data

      if (result.success) {
        const newAttachment = {
          fileName: result.data.originalname,
          filePath: result.data.filename,
          fileSize: result.data.size,
          mimeType: result.data.mimetype,
          uploadedDate: new Date()
        }

        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, newAttachment]
        }))

        toast.success('File uploaded successfully!')
      } else {
        toast.error(result.message || 'Failed to upload file')
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file. Please try again.')
    } finally {
      setUploadingFile(false)
      // Reset file input
      event.target.value = ''
    }
  }

  const handleRemoveAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }))
    toast.success('Attachment removed')
  }

  const handleDownloadAttachment = (attachment) => {
    // API base URL is like http://localhost:5000/api, so we need /documents/download
    const baseURL = api.defaults.baseURL || 'http://localhost:5000/api'
    // Remove /api from baseURL and add /api/documents/download
    const serverBase = baseURL.replace('/api', '')
    const downloadUrl = `${serverBase}/api/documents/download/${attachment.filePath}`
    window.open(downloadUrl, '_blank')
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const handleReset = () => {
    if (isEdit || isView) {
      // Reload original data
      if (claimData) {
        setFormData({
          claimNumber: claimData.claimNumber || '',
          employee: claimData.employee?._id || claimData.employee || '',
          employeeName: claimData.employeeName || '',
          employeeCode: claimData.employeeCode || '',
          department: claimData.department || '',
          claimDate: claimData.claimDate ? new Date(claimData.claimDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          expenseDate: claimData.expenseDate ? new Date(claimData.expenseDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          description: claimData.description || '',
          expenseItems: claimData.expenseItems || [],
          totalAmount: claimData.totalAmount || 0,
          totalTaxAmount: claimData.totalTaxAmount || 0,
          grandTotal: claimData.grandTotal || 0,
          currency: claimData.currency || 'INR',
          status: claimData.status || 'Draft',
          projectCode: claimData.projectCode || '',
          projectName: claimData.projectName || '',
          clientCode: claimData.clientCode || '',
          clientName: claimData.clientName || '',
          isBillable: claimData.isBillable || false,
          notes: claimData.notes || ''
        })
      }
    } else {
      // Reset to initial state
      setFormData({
        claimNumber: formData.claimNumber, // Keep the claim number
        employee: '',
        employeeName: '',
        employeeCode: '',
        department: '',
        claimDate: new Date().toISOString().split('T')[0],
        expenseDate: new Date().toISOString().split('T')[0],
        description: '',
        expenseItems: [],
        totalAmount: 0,
        totalTaxAmount: 0,
        grandTotal: 0,
        currency: 'INR',
        status: 'Draft',
        projectCode: '',
        projectName: '',
        clientCode: '',
        clientName: '',
        isBillable: false,
        notes: ''
      })
    }
  }

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Expense Claims', path: '/expenses/expense-claim', onClick: () => navigate('/expenses/expense-claim') },
    { label: isEdit ? 'Edit Claim' : isView ? 'View Claim' : 'New Claim', path: location.pathname, isCurrent: true }
  ]

  const helpContent = (
    <>
      <h3>What is Expense Claim?</h3>
      <p>This screen lets you <span className="help-term">create or edit an expense claim</span>—a request for reimbursement of out-of-pocket expenses (travel, meals, supplies, etc.). You select an employee, add one or more expense items with category and amount, then save as draft or submit for approval.</p>

      <h3>Navigation</h3>
      <p>Use the <span className="help-term">back arrow</span> in the breadcrumb or click <span className="help-term">Expenses</span> or <span className="help-term">Expense Claims</span> to return without saving.</p>

      <h3>Claim Information</h3>
      <ul>
        <li><strong>Claim Number *</strong> – Auto-generated (e.g. ECLM0001). Read-only.</li>
        <li><strong>Employee *</strong> – The employee this claim is for (required).</li>
        <li><strong>Claim Date *</strong> – Date the claim is raised.</li>
        <li><strong>Expense Date *</strong> – Date of the expense(s).</li>
        <li><strong>Description</strong> – Optional overall description of the claim.</li>
      </ul>

      <h3>Expense Items</h3>
      <p>Click <span className="help-term">+ Add Item</span> to add a line. For each item you can set:</p>
      <ul>
        <li><strong>Category *</strong> – Expense category (from master).</li>
        <li><strong>Description</strong> – Line description.</li>
        <li><strong>Amount *</strong> and <strong>Tax Amount</strong> – Totals are calculated per item and for the claim.</li>
        <li><strong>Payment Method</strong>, <strong>Receipt Number</strong>, <strong>Receipt Date</strong> – Optional.</li>
      </ul>
      <p>Use the trash icon to remove an item. Subtotal, Tax, and Grand Total are shown when you have items.</p>

      <h3>Supporting documents</h3>
      <p>After expense items, use <strong>Supporting Documents</strong> to upload receipts, invoices, or other evidence (PDF, images, Word). Multiple files are allowed. Files are saved with the claim.</p>

      <h3>Project/Client</h3>
      <p>Optionally link the claim to a <strong>Project</strong> (search by project code) and mark as <strong>Billable</strong>. You can also add <strong>Notes</strong>.</p>

      <h3>Actions</h3>
      <p><strong>Reset</strong> clears the form (keeps claim number). <strong>Save</strong> saves as draft and returns to the list. <strong>Submit for Approval</strong> (on list or after save) sends the claim to the approval workflow.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowHelp(true)}
              title="Help"
              aria-label="Open help"
            >
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Expense Claim</h1>
          <p>{isEdit ? 'Edit expense claim' : isView ? 'View expense claim' : 'Create new expense claim'}</p>
        </div>
      </div>

      <div className="form-container pay-entry">
        <div className="sac-form-header">
          <h2>Expense Claim Entry</h2>
        </div>
          
        <form className="form-content">
          <section className="pay-block">
            <div className="pay-block-head">
              <div>
                <h3>Claim</h3>
                <p>Who is claiming, and the dates this expense covers.</p>
              </div>
            </div>
          <div className="pay-grid pay-grid-4">
            <div className="form-group">
              <label htmlFor="claimNumber" className="form-label required">
                Claim Number
              </label>
              <input
                type="text"
                id="claimNumber"
                name="claimNumber"
                value={isLoadingClaimNumber ? 'Generating...' : formData.claimNumber}
                readOnly
                className="form-input pay-readonly"
              />
            </div>

            <div className="form-group">
              <label htmlFor="employee" className="form-label required">
                Employee
              </label>
              <select
                id="employee"
                name="employee"
                value={formData.employee}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView || isEdit}
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.employeeName} ({emp.employeeCode})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="claimDate" className="form-label required">
                Claim Date
              </label>
              <input
                type="date"
                id="claimDate"
                name="claimDate"
                value={formData.claimDate}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="expenseDate" className="form-label required">
                Expense Date
              </label>
              <input
                type="date"
                id="expenseDate"
                name="expenseDate"
                value={formData.expenseDate}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={isView}
              />
            </div>
          </div>
          <div className="form-group pay-follow">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-textarea"
              rows="2"
              placeholder="What this claim is for"
              disabled={isView}
            />
          </div>
          </section>

          <section className="pay-block">
            <div className="pay-block-head">
              <div>
                <h3>Expense items</h3>
                <p>Each receipt or cost on this claim.</p>
              </div>
              {!isView && (
                <button
                  type="button"
                  onClick={addExpenseItem}
                  className="btn btn-primary"
                >
                  <Plus size={16} />
                  Add item
                </button>
              )}
            </div>

          {formData.expenseItems.map((item, index) => (
            <div key={index} className="expense-item-card-modern">
              <div className="expense-item-header">
                <div className="expense-item-number">
                  <span className="item-number-badge">{index + 1}</span>
                  <span className="item-number-text">Item {index + 1}</span>
                </div>
                {!isView && (
                  <button
                    type="button"
                    onClick={() => removeExpenseItem(index)}
                    className="btn-icon-danger"
                    title="Remove item"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label className="form-label required">Category</label>
                  <select
                    value={item.category || ''}
                    onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                    className="form-input"
                    required
                    disabled={isView}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    className="form-input"
                    placeholder="Item description"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Amount</label>
                  <input
                    type="number"
                    value={item.amount || 0}
                    onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    required
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tax Amount</label>
                  <input
                    type="number"
                    value={item.taxAmount || 0}
                    onChange={(e) => handleItemChange(index, 'taxAmount', e.target.value)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    disabled={isView}
                  />
                </div>
              </div>

              <div className="pay-grid pay-grid-4 pay-follow">
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <select
                    value={item.paymentMethod || ''}
                    onChange={(e) => handleItemChange(index, 'paymentMethod', e.target.value)}
                    className="form-input"
                    disabled={isView}
                  >
                    <option value="">Select Payment Method</option>
                    {paymentMethods.map(method => (
                      <option key={method._id} value={method._id}>
                        {method.paymentMethodName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Receipt Number</label>
                  <input
                    type="text"
                    value={item.receiptNumber || ''}
                    onChange={(e) => handleItemChange(index, 'receiptNumber', e.target.value)}
                    className="form-input"
                    placeholder="Receipt number"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Receipt Date</label>
                  <input
                    type="date"
                    value={item.receiptDate ? new Date(item.receiptDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleItemChange(index, 'receiptDate', e.target.value)}
                    className="form-input"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Total</label>
                  <div className="expense-item-total-display">
                    {formatCurrency(item.totalAmount || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {formData.expenseItems.length === 0 && !isView && (
            <div className="empty-state-card">
              <div className="empty-state-icon">
                <Receipt size={48} />
              </div>
              <h3 className="empty-state-title">No expense items added</h3>
              <p className="empty-state-description">Add an item for each receipt or cost on this claim.</p>
            </div>
          )}
          </section>

          {formData.expenseItems.length > 0 && (
            <section className="pay-block">
              <div className="pay-block-head">
                <div>
                  <h3>Total</h3>
                  <p>The sum of the items on this claim, in {formData.currency || 'INR'}.</p>
                </div>
              </div>
              <div className="pay-convert-row">
                <div className="pay-facts" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none', gridTemplateColumns: '1fr 1fr' }}>
                  <div className="pay-fact">
                    <span>Subtotal</span>
                    <strong>{formatCurrency(formData.totalAmount || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </div>
                  <div className="pay-fact">
                    <span>Tax</span>
                    <strong>{formatCurrency(formData.totalTaxAmount || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </div>
                </div>
                <div className="pay-inr">
                  <span>Grand total</span>
                  <strong>{formatCurrency(formData.grandTotal || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  <p>Amount and tax added together.</p>
                </div>
              </div>
            </section>
          )}

          <section className="pay-block">
            <div className="pay-block-head">
              <div>
                <h3>Supporting documents</h3>
                <p>
                  Receipts and invoices kept with this claim
                  {formData.attachments?.length > 0 ? ` · ${formData.attachments.length} file${formData.attachments.length === 1 ? '' : 's'}` : ''}.
                </p>
              </div>
            </div>

          {!isView && (
            <div className="form-group expense-claim-attach">
              <label htmlFor="fileUpload" className="form-label">Upload evidence</label>
              <div className="file-upload-wrapper" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                <input
                  type="file"
                  id="fileUpload"
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.svg"
                  disabled={uploadingFile}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="fileUpload"
                  className={`file-upload-button ${uploadingFile ? 'disabled' : ''}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 18px',
                    backgroundColor: uploadingFile ? '#9ca3af' : '#222448',
                    color: 'white',
                    borderRadius: 0,
                    cursor: uploadingFile ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 550,
                  }}
                >
                  <Upload size={16} />
                  {uploadingFile ? 'Uploading…' : 'Choose File'}
                </label>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #5B6478)' }}>
                  PDF, DOC, DOCX, PNG, JPG, SVG · max 5 MB each · multiple files allowed
                </span>
              </div>
            </div>
          )}

          {formData.attachments && formData.attachments.length > 0 ? (
            <div className="attachments-list" style={{ marginTop: 8, marginBottom: 8 }}>
              {formData.attachments.map((attachment, index) => (
                <div
                  key={`${attachment.filePath || attachment.fileName}-${index}`}
                  className="attachment-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '12px 16px',
                    marginBottom: 8,
                    border: '1px solid #E4DFEE',
                    background: '#F8F7FB',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <FileText size={18} style={{ flexShrink: 0, color: '#222448' }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#222448', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {attachment.fileName}
                      </div>
                      <div style={{ fontSize: 12, color: '#8B93A7' }}>
                        {formatFileSize(attachment.fileSize || 0)}
                        {attachment.uploadedDate
                          ? ` · ${new Date(attachment.uploadedDate).toLocaleDateString('en-IN')}`
                          : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      title="Download"
                      onClick={() => handleDownloadAttachment(attachment)}
                    >
                      <Download size={14} />
                    </button>
                    {!isView && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        title="Remove"
                        onClick={() => handleRemoveAttachment(index)}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="empty-state-card"
              style={{ padding: '20px 16px', marginBottom: 16, textAlign: 'center' }}
            >
              <FileText size={28} style={{ color: '#8B93A7', marginBottom: 8 }} />
              <p style={{ margin: 0, fontSize: 13, color: '#5B6478' }}>
                {isView
                  ? 'No supporting documents on this claim.'
                  : 'No documents yet. Upload receipts or invoices as evidence for this claim.'}
              </p>
            </div>
          )}

          </section>

          <section className="pay-block">
            <div className="pay-block-head">
              <div>
                <h3>Project</h3>
                <p>Optional. Link this claim to a project when the cost can be billed.</p>
              </div>
            </div>
          <div className="pay-grid pay-grid-4">
            <div className="form-group">
              <label htmlFor="projectCode" className="form-label">
                Project Code
              </label>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  id="projectCode"
                  name="projectCode"
                  value={formData.projectCode}
                  readOnly
                  className="form-input search-input"
                  placeholder="Select project"
                  style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                  disabled={isView}
                />
                <button
                  type="button"
                  className="search-button"
                  title="Search Project"
                  onClick={handleProjectSearch}
                  disabled={isView}
                >
                  <Search size={16} />
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="projectName" className="form-label">
                Project Name
              </label>
              <input
                type="text"
                id="projectName"
                name="projectName"
                value={formData.projectName}
                readOnly
                className="form-input"
                placeholder="Project name"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="clientCode" className="form-label">
                Client Code
              </label>
              <input
                type="text"
                id="clientCode"
                name="clientCode"
                value={formData.clientCode}
                readOnly
                className="form-input"
                placeholder="Client code"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                disabled={isView}
              />
            </div>

            <div className="form-group">
              <label htmlFor="isBillable" className="form-label">
                <input
                  type="checkbox"
                  id="isBillable"
                  name="isBillable"
                  checked={formData.isBillable}
                  onChange={handleInputChange}
                  style={{ marginRight: '8px' }}
                  disabled={isView}
                />
                Billable to Client
              </label>
            </div>
          </div>
          <div className="form-group pay-follow">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="form-textarea"
              rows="3"
              placeholder="Anything else that should stay with this claim"
              disabled={isView}
            />
          </div>
          </section>

          {/* Action Buttons */}
          {!isView && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleReset}
                className="btn-secondary"
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn-primary"
                disabled={loading}
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Project Selection Modal */}
      <ProjectSelectionModal
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        onSelectProject={handleProjectSelect}
        selectedProjectCode={formData.projectCode}
      />

      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title="Expense Claim – Help"
      >
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ExpenseClaimForm

